import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Clock, Loader2, MapPin, X } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import SectionHead from "@/components/SectionHead";
import { Reveal, EASE } from "@/components/Reveal";
import CandidateForm from "@/components/forms/CandidateForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_CLS = {
  IT: "border-sky-400/25 bg-sky-400/[0.07] text-sky-300",
  "Non-IT": "border-indigo-400/25 bg-indigo-400/[0.07] text-indigo-300",
  Leadership: "border-amber-400/25 bg-amber-400/[0.07] text-amber-300",
};

export default function Jobs() {
  usePageMeta(
    "Current Openings | IT & Non-IT Jobs Across India — Talentify",
    "Browse current job openings across India curated by Talentify and apply directly with your resume."
  );
  const [jobs, setJobs] = useState(null);
  const [cat, setCat] = useState("all");
  const [applyJob, setApplyJob] = useState(null);

  useEffect(() => {
    fetch(`${API}/jobs`)
      .then((r) => r.json())
      .then(setJobs)
      .catch(() => setJobs([]));
  }, []);

  const cats = ["all", ...new Set((jobs || []).map((j) => j.category).filter(Boolean))];
  const shown = (jobs || []).filter((j) => cat === "all" || j.category === cat);

  return (
    <main>
      <section className="relative overflow-hidden pb-24 pt-40">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% -20%, rgba(56,189,248,0.1), transparent 55%)" }} />
        <div className="relative mx-auto max-w-5xl px-6">
          <SectionHead
            overline="Current Openings"
            title="Roles We're Hiring For Right Now"
            sub="Live mandates from organisations across India. Apply directly — our recruitment team reviews every profile."
            testid="jobs-heading"
          />

          {jobs === null ? (
            <div className="grid place-items-center py-20">
              <Loader2 className="animate-spin text-sky-400" size={26} />
            </div>
          ) : jobs.length === 0 ? (
            <Reveal>
              <div className="glass-card rounded-3xl p-12 text-center" data-testid="jobs-empty-state">
                <p className="font-display text-xl font-semibold text-white">No open positions listed right now.</p>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-400">
                  New mandates arrive regularly. Submit your resume and our team will connect you with relevant opportunities as they open.
                </p>
                <a
                  href="/candidates"
                  data-testid="jobs-empty-cta"
                  className="group mt-7 inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-blue-500"
                >
                  Submit Your Resume <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
            </Reveal>
          ) : (
            <>
              <div className="mb-8 flex flex-wrap gap-2">
                {cats.map((c) => (
                  <button
                    key={c}
                    data-testid={`jobs-filter-${c}`}
                    onClick={() => setCat(c)}
                    className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-300 ${
                      cat === c ? "bg-blue-600 text-white" : "border border-white/10 text-slate-400 hover:text-white"
                    }`}
                  >
                    {c === "all" ? "All Roles" : c}
                  </button>
                ))}
              </div>
              <div className="space-y-4" data-testid="jobs-list">
                {shown.map((j, i) => (
                  <Reveal key={j.id} delay={Math.min(i * 0.06, 0.3)}>
                    <div
                      data-testid={`job-card-${i}`}
                      className="group flex flex-col gap-5 rounded-3xl border border-white/[0.08] bg-[#0A0F1D]/70 p-7 transition-[border-color,transform] duration-500 hover:-translate-y-0.5 hover:border-sky-400/30 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <div className="flex flex-wrap items-center gap-3">
                          <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${CAT_CLS[j.category] || CAT_CLS.IT}`}>
                            {j.category}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-slate-500"><MapPin size={12} /> {j.location || "PAN India"}</span>
                          {j.experience && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Clock size={12} /> {j.experience}</span>}
                        </div>
                        <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-white">{j.title}</h3>
                        {j.description && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">{j.description}</p>}
                      </div>
                      <button
                        data-testid={`job-apply-${i}`}
                        onClick={() => setApplyJob(j)}
                        className="group/btn inline-flex shrink-0 items-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-blue-500"
                      >
                        Apply Now <ArrowRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                      </button>
                    </div>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <AnimatePresence>
        {applyJob && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] grid place-items-center overflow-y-auto bg-black/70 px-4 py-10 backdrop-blur-sm"
            onClick={() => setApplyJob(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="glass-card relative w-full max-w-2xl rounded-3xl p-7 sm:p-9"
              onClick={(e) => e.stopPropagation()}
              data-testid="job-apply-modal"
            >
              <button
                data-testid="job-apply-close"
                onClick={() => setApplyJob(null)}
                aria-label="Close"
                className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-400 transition-colors hover:border-white/30 hover:text-white"
              >
                <X size={16} />
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-400">Applying for</p>
              <h3 className="mb-6 mt-1.5 font-display text-xl font-bold tracking-tight text-white">{applyJob.title}</h3>
              <CandidateForm job={applyJob} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
